import { Button, Input, Badge } from "@aix/ui"
import { useState, useEffect, useRef } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"
import {
  MessageSquare,
  Calendar,
  BarChart3,
  TrendingUp,
  Copy,
  RefreshCw,
  Download,
  Search,
  Trash2,
  Menu,
  X,
  Info,
  Target,
  Edit3,
} from "lucide-react"
import type { Coach } from "../../../types/coach-os.types"
import IntakeContextPanel from "./IntakeContextPanel"
import IntakeProgressHeader from "./IntakeProgressHeader"
import DraftPlanReviewModal from "./DraftPlanReviewModal"
import { useNavigate, useSearch } from "@tanstack/react-router"

interface ChatTabProps {
  coachId: string
  coach: Coach
}

export default function ChatTab({ coachId, coach }: ChatTabProps) {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string; conversationId?: string }
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(search.conversationId || null)
  const [inputMessage, setInputMessage] = useState("")
  const [showNewConversationModal, setShowNewConversationModal] = useState(false)
  const [newConvType, setNewConvType] = useState<"daily" | "weekly" | "monthly" | "quarterly" | "ad-hoc">("ad-hoc")
  const [conversationTypeFilter, setConversationTypeFilter] = useState<string>("all")
  const [conversationSearchQuery, setConversationSearchQuery] = useState("")
  const [showDeleted, setShowDeleted] = useState(false)
  const [showSidebar, setShowSidebar] = useState(false)
  const [showContextDrawer, setShowContextDrawer] = useState(false)
  const [showDraftPlanModal, setShowDraftPlanModal] = useState(false)
  const [draftPlanId, setDraftPlanId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const utils = trpc.useUtils()

  // Load conversations for this coach
  const { data: conversationsData, isLoading: loadingConversations } = trpc.coachChat.list.useQuery({
    coachId,
    limit: 50,
    conversationType: conversationTypeFilter === "all" ? undefined : (conversationTypeFilter as any),
    includeDeleted: showDeleted,
  })

  // Load active conversation with messages
  const { data: conversationData, isLoading: loadingMessages } = trpc.coachChat.get.useQuery(
    {
      conversationId: selectedConversationId,
      messagesLimit: 100,
    },
    {
      enabled: !!selectedConversationId,
    },
  )

  // Load conversation context
  const { data: contextData, isLoading: loadingContext } = trpc.coachChat.getContext.useQuery(
    {
      conversationId: selectedConversationId,
    },
    {
      enabled: !!selectedConversationId,
    },
  )

  // Check for draft quarterly plan
  const { data: draftPlansData } = trpc.plans.quarterlyPlans.list.useQuery(
    {
      coachId,
      status: "draft",
      limit: 1,
    },
    {
      enabled: coach.intakeStatus === "completed",
    },
  )

  const hasDraftPlan = draftPlansData?.quarterlyPlans && draftPlansData.quarterlyPlans.length > 0

  // Mutations
  const createConversationMutation = trpc.coachChat.create.useMutation()
  const sendMessageMutation = trpc.coachChat.sendMessage.useMutation()
  const deleteConversationMutation = trpc.coachChat.delete.useMutation()
  const restoreConversationMutation = trpc.coachChat.restore.useMutation()

  const generatePlanMutation = trpc.plans.quarterlyPlans.generateFromIntake.useMutation({
    onSuccess: (plan) => {
      setDraftPlanId(plan.id)
      setShowDraftPlanModal(true)
      toast.success("Quarterly plan generated! Review it below.")
      void utils.plans.quarterlyPlans.list.invalidate({ coachId })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to generate plan")
    },
  })

  // Helper function to update selected conversation and URL
  const selectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId)
    navigate({
      // @ts-ignore
      search: (prev) => ({ ...prev, conversationId }),
    })
  }

  // Sync URL conversationId changes to local state
  useEffect(() => {
    if (search.conversationId && search.conversationId !== selectedConversationId) {
      setSelectedConversationId(search.conversationId)
    }
  }, [search.conversationId])

  // Auto-select first conversation OR auto-create ad-hoc conversation
  useEffect(() => {
    if (!loadingConversations && conversationsData) {
      if (conversationsData.conversations.length > 0 && !selectedConversationId) {
        selectConversation(conversationsData.conversations[0].id)
      } else if (
        conversationsData.conversations.length === 0 &&
        !selectedConversationId &&
        !createConversationMutation.isLoading
      ) {
        // Auto-create ad-hoc conversation if none exist
        createConversationMutation.mutate(
          {
            coachId,
            conversationType: "ad-hoc",
            title: `Chat with ${coach.name}`,
          },
          {
            onSuccess: (newConv) => {
              selectConversation(newConv.id)
              void utils.coachChat.list.invalidate({ coachId })
            },
            onError: () => {
              toast.error("Failed to create initial conversation")
            },
          },
        )
      }
    }
  }, [
    conversationsData,
    selectedConversationId,
    loadingConversations,
    coachId,
    coach.name,
    createConversationMutation,
    utils,
  ])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [conversationData?.messages])

  const handleCreateConversation = async () => {
    try {
      const newConv = await createConversationMutation.mutateAsync({
        coachId,
        conversationType: newConvType,
        title: `${newConvType.charAt(0).toUpperCase() + newConvType.slice(1)} chat with ${coach.name}`,
      })
      selectConversation(newConv.id)
      setShowNewConversationModal(false)
      void utils.coachChat.list.invalidate({ coachId })
      toast.success("New conversation created!")
    } catch (error) {
      toast.error("Failed to create conversation")
    }
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedConversationId) return

    const messageContent = inputMessage
    setInputMessage("") // Clear input immediately for better UX

    try {
      // Send message to coach
      await sendMessageMutation.mutateAsync({
        conversationId: selectedConversationId,
        content: messageContent,
      })

      void utils.coachChat.get.invalidate({ conversationId: selectedConversationId })
    } catch (error) {
      toast.error("Failed to send message")
    }
  }

  const handleCopyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content)
      toast.success("Copied to clipboard!")
    } catch (error) {
      toast.error("Failed to copy")
    }
  }

  const handleRegenerateResponse = async (messageId: string) => {
    if (!selectedConversationId) return

    try {
      // Find the user message that triggered this response
      const messages = conversationData?.messages || []
      const messageIndex = messages.findIndex((m) => m.id === messageId)
      if (messageIndex <= 0) return

      const userMessage = messages[messageIndex - 1]
      if (userMessage.role !== "user") return

      // Re-send the user message to get a new response
      await sendMessageMutation.mutateAsync({
        conversationId: selectedConversationId,
        content: userMessage.content,
      })
      void utils.coachChat.get.invalidate({ conversationId: selectedConversationId })
      toast.success("Response regenerated!")
    } catch (error) {
      toast.error("Failed to regenerate")
    }
  }

  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent selecting the conversation

    if (
      !confirm(
        "Are you sure you want to delete this conversation? It can be restored later from the deleted conversations view.",
      )
    ) {
      return
    }

    try {
      await deleteConversationMutation.mutateAsync({ conversationId })

      // Clear selection if we deleted the active conversation
      if (selectedConversationId === conversationId) {
        setSelectedConversationId(null)
        // Remove conversationId from URL
        navigate({
          // @ts-ignore
          search: (prev) => {
            const { conversationId, ...rest } = prev
            return rest
          },
        })
      }

      // Refresh conversation list
      void utils.coachChat.list.invalidate({ coachId })
      toast.success("Conversation deleted")
    } catch (error) {
      toast.error("Failed to delete conversation")
    }
  }

  const handleRestoreConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent selecting the conversation

    try {
      await restoreConversationMutation.mutateAsync({ conversationId })

      // Refresh conversation list
      void utils.coachChat.list.invalidate({ coachId })
      toast.success("Conversation restored")
    } catch (error) {
      toast.error("Failed to restore conversation")
    }
  }

  const handleExportConversation = () => {
    if (!conversationData) return

    try {
      const exportData = {
        coach: coach.name,
        conversation: conversationData.conversation.title,
        conversationType: conversationData.conversation.conversationType,
        exportedAt: new Date().toISOString(),
        messages: conversationData.messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.createdAt,
        })),
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `coach-chat-${coach.name.toLowerCase().replace(/\s+/g, "-")}-${
        new Date().toISOString().split("T")[0]
      }.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      toast.success("Conversation exported!")
    } catch (error) {
      toast.error("Failed to export conversation")
    }
  }

  // Filter and search conversations
  const filteredConversations =
    conversationsData?.conversations.filter((conv) => {
      const matchesSearch = conv.title.toLowerCase().includes(conversationSearchQuery.toLowerCase())
      return matchesSearch
    }) || []

  // Get last message for a conversation
  const getLastMessage = (conversation: any) => {
    if (!conversation.lastMessage) {
      return "No messages yet..."
    }

    const content = conversation.lastMessage.content
    const role = conversation.lastMessage.role

    // Truncate long messages
    const truncated = content.length > 60 ? content.substring(0, 60) + "..." : content

    // Add role prefix
    if (role === "user") {
      return `You: ${truncated}`
    } else if (role === "assistant") {
      return `${coach.name}: ${truncated}`
    } else {
      return truncated
    }
  }

  const getConversationIcon = (type: string) => {
    switch (type) {
      case "intake":
        return "📝"
      case "daily":
        return "☀️"
      case "weekly":
        return "📅"
      case "monthly":
        return "📊"
      case "quarterly":
        return "🎯"
      default:
        return "💬"
    }
  }

  const formatTimeAgo = (date: Date | string) => {
    const now = new Date()
    const messageDate = typeof date === "string" ? new Date(date) : date
    const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 1) return "Just now"
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return messageDate.toLocaleDateString()
  }

  return (
    <div className="flex h-[calc(100vh-200px)] gap-4 relative">
      {/* Mobile Sidebar Toggle Button */}
      <button
        onClick={() => setShowSidebar(!showSidebar)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-primary text-primary-foreground p-2 rounded-lg shadow-lg"
      >
        {showSidebar ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile Overlay */}
      {showSidebar && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setShowSidebar(false)} />
      )}

      {/* Conversation Sidebar */}
      <div
        className={`
        w-80 bg-secondary rounded-lg p-4 flex flex-col
        fixed lg:relative inset-y-0 left-0 z-40
        transform transition-transform duration-300 ease-in-out
        ${showSidebar ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base md:text-lg font-semibold text-foreground">Conversations</h3>
          <Button
            onClick={() => setShowNewConversationModal(true)}
            size="sm"
            className="h-8 px-2 md:px-3 text-xs md:text-sm"
            loading={createConversationMutation.isLoading}
          >
            New
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="space-y-2 mb-4">
          <div className="relative">
            <Search className="absolute left-2 md:left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search conversations..."
              value={conversationSearchQuery}
              onChange={(e) => setConversationSearchQuery(e.target.value)}
              className="pl-7 md:pl-9 h-8 md:h-9 text-xs md:text-sm"
            />
          </div>
          <select
            value={conversationTypeFilter}
            onChange={(e) => setConversationTypeFilter(e.target.value)}
            className="w-full bg-background border border-border rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm text-foreground"
          >
            <option value="all">All Types</option>
            <option value="intake">📝 Intake</option>
            <option value="daily">☀️ Daily</option>
            <option value="weekly">📅 Weekly</option>
            <option value="monthly">📊 Monthly</option>
            <option value="quarterly">🎯 Quarterly</option>
            <option value="ad-hoc">💬 Ad-hoc</option>
          </select>
          <label className="flex items-center gap-2 bg-background border border-border rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-foreground">Show deleted</span>
          </label>
        </div>

        {loadingConversations ? (
          <div className="text-center text-muted-foreground py-8">Loading...</div>
        ) : filteredConversations.length > 0 ? (
          <div className="flex-1 overflow-y-auto space-y-2">
            {filteredConversations.map((conv) => (
              <div
                key={conv.id}
                className={`relative group rounded-lg transition-colors ${
                  selectedConversationId === conv.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-background hover:bg-primary/10"
                }`}
              >
                <button onClick={() => selectConversation(conv.id)} className="w-full text-left p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{getConversationIcon(conv.conversationType)}</span>
                    <span className="font-medium text-sm truncate flex-1">{conv.title}</span>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        selectedConversationId === conv.id ? "border-primary-foreground/30 text-primary-foreground" : ""
                      }`}
                    >
                      {conv.conversationType}
                    </Badge>
                  </div>
                  <div className="text-xs opacity-70 truncate mb-1">{getLastMessage(conv)}</div>
                  <div className="flex items-center justify-between text-xs opacity-80">
                    <span>{conv.messageCount} messages</span>
                    <span>{formatTimeAgo(conv.updatedAt)}</span>
                  </div>
                </button>

                {/* Delete/Restore button - shows on hover */}
                {(conv as any).deletedAt ? (
                  <button
                    onClick={(e) => handleRestoreConversation(conv.id, e)}
                    className={`absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                      selectedConversationId === conv.id
                        ? "hover:bg-primary-foreground/20 text-primary-foreground"
                        : "hover:bg-primary/20 text-primary"
                    }`}
                    title="Restore conversation"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={(e) => handleDeleteConversation(conv.id, e)}
                    className={`absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                      selectedConversationId === conv.id
                        ? "hover:bg-primary-foreground/20 text-primary-foreground"
                        : "hover:bg-destructive/20 text-destructive"
                    }`}
                    title="Delete conversation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No conversations found</p>
            <p className="text-xs mt-1">
              {conversationSearchQuery ? "Try a different search" : "Create one to start chatting!"}
            </p>
          </div>
        )}
      </div>

      {/* Main Chat Area + Context Panel */}
      <div className="flex-1 flex gap-0 w-full lg:w-auto">
        {/* Chat Area */}
        <div
          className={`bg-secondary rounded-lg flex flex-col w-full ${
            conversationData?.conversation.conversationType === "intake" ? "lg:flex-1" : ""
          }`}
        >
          {selectedConversationId ? (
            <>
              {/* Chat Header */}
              <div className="p-3 md:p-4 border-b border-border">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg md:text-xl font-bold text-foreground flex items-center gap-2 truncate">
                      <span>{coach.icon}</span>
                      <span className="truncate">{conversationData?.conversation.title || "Loading..."}</span>
                    </h2>
                    <p className="text-xs md:text-sm text-muted-foreground truncate">
                      {coach.tone} tone • {conversationData?.conversation.conversationType} conversation
                    </p>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    {conversationData?.conversation.conversationType !== "intake" && contextData && (
                      <Button
                        onClick={() => setShowContextDrawer(!showContextDrawer)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-xs md:text-sm flex-1 sm:flex-initial"
                      >
                        <Info className="w-3 h-3 md:w-4 md:h-4" />
                        Context
                      </Button>
                    )}
                    <Button
                      onClick={handleExportConversation}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 text-xs md:text-sm flex-1 sm:flex-initial"
                    >
                      <Download className="w-3 h-3 md:w-4 md:h-4" />
                      Export
                    </Button>
                  </div>
                </div>
              </div>

              {/* Intake Progress Header */}
              {coach.intakeStatus === "in-progress" && (
                <IntakeProgressHeader coachId={coachId} coachName={coach.name} />
              )}

              {/* Generate Quarterly Plan CTA */}
              {coach.intakeStatus === "completed" && !hasDraftPlan && (
                <div className="mx-3 md:mx-6 my-4 bg-secondary border-2 border-primary/20 rounded-lg p-4 md:p-6">
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="text-3xl md:text-4xl flex-shrink-0">🎯</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-semibold mb-2">Ready to Create Your Quarterly Plan?</h3>
                      <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">
                        I can draft a 12-week plan based on everything we discussed in your intake. You&apos;ll be able
                        to review and edit it before finalizing.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                        <Button
                          onClick={() => generatePlanMutation.mutate({ coachId, durationWeeks: 12 })}
                          loading={generatePlanMutation.isLoading}
                          className="bg-primary text-primary-foreground text-xs md:text-sm"
                          leftIcon={<Target className="w-3 h-3 md:w-4 md:h-4" />}
                        >
                          Generate My Plan
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => navigate({ to: "/coach-os", search: (s) => ({ ...s, tab: "plans" }) })}
                          className="text-xs md:text-sm"
                        >
                          Create Manually Instead
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Draft Plan Review Prompt */}
              {coach.intakeStatus === "completed" && hasDraftPlan && (
                <div className="mx-3 md:mx-6 my-4 bg-primary/10 border-2 border-primary rounded-lg p-4 md:p-6">
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="text-3xl md:text-4xl flex-shrink-0">📋</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-semibold mb-2">Your Draft Plan is Ready!</h3>
                      <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">
                        I&apos;ve created a quarterly plan from your intake. Review it, make any edits, and accept when
                        ready.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                        <Button
                          onClick={() => {
                            setDraftPlanId(draftPlansData.quarterlyPlans[0].id)
                            setShowDraftPlanModal(true)
                          }}
                          className="bg-primary text-primary-foreground text-xs md:text-sm"
                          leftIcon={<Edit3 className="w-3 h-3 md:w-4 md:h-4" />}
                        >
                          Review Draft Plan
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => navigate({ to: "/coach-os", search: (s) => ({ ...s, tab: "plans" }) })}
                          className="text-xs md:text-sm"
                        >
                          View All Plans
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-3 md:p-6 bg-background">
                {loadingMessages ? (
                  <div className="text-center text-muted-foreground py-8 text-sm md:text-base">Loading messages...</div>
                ) : conversationData && conversationData.messages.length > 0 ? (
                  <div className="space-y-3 md:space-y-4">
                    {conversationData.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} group`}
                      >
                        <div
                          className={`max-w-[85%] sm:max-w-[70%] rounded-lg p-2 md:p-3 relative ${
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-foreground border border-border"
                          }`}
                        >
                          <div className="whitespace-pre-wrap text-sm md:text-base">{msg.content}</div>
                          <div className="text-xs opacity-60 mt-1.5 md:mt-2 flex items-center justify-between gap-2">
                            <span>{formatTimeAgo(msg.createdAt)}</span>
                            {msg.role === "assistant" && (
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => handleCopyMessage(msg.content)}
                                  className="p-1 hover:bg-foreground/10 rounded"
                                  title="Copy message"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleRegenerateResponse(msg.id)}
                                  className="p-1 hover:bg-foreground/10 rounded"
                                  title="Regenerate response"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="text-6xl mb-4">{coach.icon}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Start a conversation with {coach.name}
                    </h3>
                    <p className="text-muted-foreground max-w-md">
                      Your coach will respond in a {coach.tone} tone and can help you with planning, motivation, and
                      analyzing your progress.
                    </p>
                  </div>
                )}
              </div>

              {/* Quick Reply Buttons - Only show for last assistant message in intake conversations */}
              {conversationData?.conversation.conversationType === "intake" &&
                conversationData?.messages.length > 0 &&
                (() => {
                  const lastMessage = conversationData.messages[conversationData.messages.length - 1]
                  const quickReplies = lastMessage?.metadata?.quickReplies as
                    | { label: string; value: string | null }[]
                    | undefined

                  return (
                    lastMessage.role === "assistant" &&
                    quickReplies &&
                    quickReplies.length > 0 && (
                      <div className="px-3 md:px-4 pb-2 border-t border-border bg-secondary pt-3">
                        <div className="flex flex-wrap gap-2">
                          {quickReplies.map((reply: { label: string; value: string | null }, idx: number) => (
                            <Button
                              key={idx}
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (reply.value === null) {
                                  // "Custom" option - just focus the textarea
                                  const textarea = document.querySelector("textarea")
                                  if (textarea) {
                                    textarea.focus()
                                  }
                                } else {
                                  // Auto-fill with the quick reply value
                                  setInputMessage(reply.value)
                                  // Optionally auto-submit here if desired
                                }
                              }}
                              className="text-xs"
                            >
                              {reply.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )
                  )
                })()}

              {/* Input Area */}
              <div className="p-3 md:p-4 border-t border-border bg-secondary">
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3 items-stretch sm:items-start">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault()
                        void handleSendMessage()
                      }
                    }}
                    placeholder={`Ask ${coach.name} anything... (Shift+Enter for new line)`}
                    className="flex-1 bg-background border border-border rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
                    rows={2}
                  />
                  <Button
                    onClick={handleSendMessage}
                    loading={sendMessageMutation.isLoading}
                    disabled={!inputMessage.trim() || sendMessageMutation.isLoading}
                    className="text-xs md:text-sm w-full sm:w-auto"
                  >
                    Send
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50 text-muted-foreground" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No conversation selected</h3>
                <p className="text-muted-foreground mb-4">Select a conversation from the sidebar or create a new one</p>
                <Button onClick={() => setShowNewConversationModal(true)}>Create New Conversation</Button>
              </div>
            </div>
          )}
        </div>

        {/* Intake Context Panel (for intake conversations only) */}
        {conversationData?.conversation.conversationType === "intake" && <IntakeContextPanel coach={coach} />}
      </div>

      {/* Context Drawer Overlay */}
      {showContextDrawer && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowContextDrawer(false)} />
      )}

      {/* Context Drawer - Side drawer on desktop (lg+), bottom drawer on mobile */}
      {selectedConversationId &&
        contextData &&
        conversationData?.conversation.conversationType !== "intake" &&
        showContextDrawer && (
          <div
            className={`
          fixed z-50 bg-secondary overflow-y-auto
          lg:top-0 lg:right-0 lg:h-full lg:w-96
          bottom-0 left-0 right-0 max-h-[70vh] rounded-t-2xl lg:rounded-none
          transform transition-transform duration-300 ease-in-out
          ${showContextDrawer ? "translate-y-0 lg:translate-x-0" : "translate-y-full lg:translate-x-full"}
        `}
          >
            {/* Drawer Header */}
            <div className="sticky top-0 bg-secondary border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Context</h3>
              <button
                onClick={() => setShowContextDrawer(false)}
                className="p-2 hover:bg-background rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="p-4">
              {/* Current Plan */}
              {contextData.currentWeeklyPlan && (
                <div className="mb-4 p-3 bg-background rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">Current Week</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Week {contextData.currentWeeklyPlan.weekNumber}</p>
                  <p className="text-xs text-muted-foreground">{contextData.currentWeeklyPlan.status}</p>
                </div>
              )}

              {/* Recent Check-ins */}
              {contextData.recentCheckIns && contextData.recentCheckIns.length > 0 && (
                <div className="mb-4 p-3 bg-background rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">Recent Check-ins</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{contextData.recentCheckIns.length} in last 7 days</p>
                </div>
              )}

              {/* Recent Insights */}
              {contextData.recentInsights && contextData.recentInsights.length > 0 && (
                <div className="mb-4 p-3 bg-background rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">Recent Insights</span>
                  </div>
                  {contextData.recentInsights.map((insight) => (
                    <div key={insight.id} className="mt-2 text-xs">
                      <p className="font-medium">{insight.title}</p>
                      <p className="text-muted-foreground truncate">{insight.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Trackables */}
              {contextData.trackables && contextData.trackables.length > 0 && (
                <div className="p-3 bg-background rounded-lg">
                  <span className="font-semibold text-sm">Active Trackables</span>
                  <div className="mt-2 space-y-1">
                    {contextData.trackables.map((trackable) => (
                      <div key={trackable.id} className="text-xs text-muted-foreground">
                        • {trackable.promptText}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Draft Plan Review Modal */}
      {showDraftPlanModal && draftPlanId && (
        <DraftPlanReviewModal
          quarterlyPlanId={draftPlanId}
          coachId={coachId}
          coachName={coach.name}
          onClose={() => {
            setShowDraftPlanModal(false)
            setDraftPlanId(null)
          }}
          onAccepted={() => {
            setShowDraftPlanModal(false)
            setDraftPlanId(null)
            void utils.plans.quarterlyPlans.list.invalidate({ coachId })
            void utils.plans.weeklyPlans.list.invalidate({ coachId })
            // Navigate to plans tab to see the activated plan
            navigate({ to: "/coach-os", search: (s) => ({ ...s, tab: "plans" }) })
          }}
        />
      )}

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-foreground mb-4">New Conversation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Conversation Type</label>
                <select
                  value={newConvType}
                  onChange={(e) =>
                    setNewConvType(e.target.value as "daily" | "weekly" | "monthly" | "quarterly" | "ad-hoc")
                  }
                  className="w-full bg-secondary border border-border rounded-lg px-3 py-2 text-foreground"
                >
                  <option value="daily">Daily ☀️ - Quick daily reflection</option>
                  <option value="weekly">Weekly 📅 - Progress review</option>
                  <option value="monthly">Monthly 📊 - Deeper reflection</option>
                  <option value="quarterly">Quarterly 🎯 - Big picture</option>
                  <option value="ad-hoc">Ad-hoc 💬 - General questions</option>
                </select>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleCreateConversation} loading={createConversationMutation.isLoading}>
                  Create
                </Button>
                <Button onClick={() => setShowNewConversationModal(false)} className="bg-secondary">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
